import { useEffect } from 'react';
import {
  MediaPlayerInstance,
  TextTrack,
  useMediaRemote,
} from '@vidstack/react';
import { RefObject } from 'react';

interface TextTrackConfig {
  src: string;
  kind: 'captions' | 'metadata';
  label: string;
  language: string;
  type: 'vtt';
  default?: boolean;
}

/**
 * Hook to manage text tracks - clears existing tracks and adds new ones
 * @param playerRef - Ref to the MediaPlayerInstance
 * @param remote - Media remote instance from useMediaRemote
 * @param showClosedCaptions - Whether closed captions should be displayed
 * @param source - Media source (used as dependency)
 * @param tracks - Array of text track configurations to add
 */
export const useTextTracks = (
  playerRef: RefObject<MediaPlayerInstance | null>,
  remote: ReturnType<typeof useMediaRemote>,
  showClosedCaptions: boolean,
  source: string | null,
  tracks: TextTrackConfig[]
): void => {
  useEffect(() => {
    playerRef.current?.textTracks.clear();

    if (tracks.length === 0) {
      return;
    }

    const textTracks = tracks.map(
      (config) =>
        new TextTrack({
          src: config.src,
          kind: config.kind,
          label: config.label,
          language: config.language,
          type: config.type,
          default: config.default,
        })
    );

    if (playerRef.current) {
      textTracks.forEach((track) => {
        playerRef.current?.textTracks.add(track);
      });

      // Set text track mode after tracks are added
      if (remote && tracks.some((t) => t.kind === 'captions')) {
        const timer = setTimeout(() => {
          remote.changeTextTrackMode(
            0,
            showClosedCaptions ? 'showing' : 'hidden'
          );
        }, 100);
        return () => clearTimeout(timer);
      }
    }
  }, [source, tracks, remote, showClosedCaptions, playerRef]);
};
